import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Card,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  Paper,
  Button,
  TableHead,
  IconButton,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import { Link } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { API_ENDPOINTS } from "../../../config/api";
import { useAuth } from "../../../context/AuthContext";

const API_URL = API_ENDPOINTS.PAYMENT_TERMS;

const PaymentTermsListContent = () => {
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { user } = useAuth();
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: null,
    name: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchPaymentTerms();
  }, []);

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const fetchPaymentTerms = async () => {
    try {
      const response = await axios.get(API_URL.BASE);
      const data = response.data;
      let paymentTermsArray = [];
      if (Array.isArray(data)) {
        paymentTermsArray = data;
      } else if (data && Array.isArray(data.paymentTerms)) {
        paymentTermsArray = data.paymentTerms;
      }
      setPaymentTerms(paymentTermsArray);
    } catch (error) {
      console.error("Error fetching payment terms:", error);
      setSnackbar({
        open: true,
        message: "Failed to load payment terms",
        severity: "error",
      });
      setPaymentTerms([]);
    }
  };

  const handleDeleteClick = (id, name) => {
    setDeleteDialog({
      open: true,
      id,
      name,
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(API_URL.DELETE(deleteDialog.id));
      setPaymentTerms((prevPaymentTerms) => 
        prevPaymentTerms.filter((paymentTerm) => paymentTerm.id !== deleteDialog.id)
      );
      setSnackbar({
        open: true,
        message: "Payment term deleted successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error deleting payment term:", error);
      setSnackbar({
        open: true,
        message: "Failed to delete payment term",
        severity: "error",
      });
    }
    setDeleteDialog({ open: false, id: null, name: "" });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, id: null, name: "" });
  };

  const handleTogglePublish = async (id, currentStatus) => {
    try {
      await axios.put(API_URL.EDIT(id), { publish: !currentStatus });
      setPaymentTerms((prevPaymentTerms) =>
        prevPaymentTerms.map((paymentTerm) =>
          paymentTerm.id === id ? { ...paymentTerm, publish: !currentStatus } : paymentTerm
        )
      );
      setSnackbar({
        open: true,
        message: `Payment term ${!currentStatus ? "published" : "unpublished"} successfully`,
        severity: "success",
      });
    } catch (error) {
      console.error("Error updating publish status:", error);
      setSnackbar({
        open: true,
        message: "Failed to update publish status",
        severity: "error",
      });
    }
  };

  const filteredPaymentTerms = Array.isArray(paymentTerms)
    ? paymentTerms.filter((paymentTerm) =>
        paymentTerm.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  return (
    <Card sx={{ boxShadow: "none", borderRadius: "7px", mb: "25px", padding: "25px" }}>
      {/* <NavList /> */}
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: "25px" }}>
          <input
            type="text"
            placeholder="Search payment term..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: "8px", width: "200px", borderRadius: "5px", border: "1px solid #ccc" }}
          />
          {user.permissions.includes("add-paymentTerms") && (  
            <Link to="/ecommerce/create-payment-term">
              <Button variant="outlined" color="primary">
                <AddIcon /> Add New Payment Term
              </Button>
            </Link>
          )}
        </Box>

        <TableContainer className="custom-table" component={Paper}>
          <Table sx={{ minWidth: 1000 }} aria-label="Payment Terms Table">    
            <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Publish</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPaymentTerms
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((paymentTerm, index) => (
                  <TableRow key={paymentTerm.id}>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{paymentTerm.name}</TableCell>
                    <TableCell>
                      <Switch
                        checked={paymentTerm.publish}
                        onChange={() => handleTogglePublish(paymentTerm.id, paymentTerm.publish)}
                        color="success"
                      />
                    </TableCell>
                    <TableCell>
                      {/* {user.permissions.includes("view-paymentTerms") && (
                        <Link to={`/ecommerce/payment-terms-list/details/${paymentTerm.id}`}>
                          <IconButton color="info">
                            <VisibilityIcon />
                          </IconButton>
                        </Link>
                      )} */}
                      {user.permissions.includes("edit-paymentTerms") && (
                        <Link to={`/ecommerce/edit-payment-term/${paymentTerm.id}`}>
                          <IconButton color="primary">
                            <EditIcon />
                          </IconButton>
                        </Link>
                      )}
                      {user.permissions.includes("delete-paymentTerms") && (
                        <IconButton 
                          color="error" 
                          onClick={() => handleDeleteClick(paymentTerm.id, paymentTerm.name)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                  count={filteredPaymentTerms.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the payment term "{deleteDialog.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default PaymentTermsListContent;